/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Timestamp } from '../models/Timestamp';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class UtilsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current server time.
     * This endpoint provides the current server time, necessary for calculating the client-server offset (cs_offset). This offset is crucial for estimating the server time on the client side (Tcest).
     *
     * **Calculating Client-Server Offset (cs_offset)**
     *
     * 1. **Sample Collection**: Obtain N samples of server time (Ts) from the endpoint. More samples improve accuracy but increase estimation time. A good starting point is 30 samples.
     * 2. **Round-Trip Delay (RTD) Measurement**: For each sample, record the send time (Tsend) and receive time (Treceive). Calculate RTD as Treceive - Tsend.
     * 3. **Server Time Estimation (Ts_est)**: Estimate the server time at response receipt by adding half of the RTD to the server time (Ts). Formula: Ts_est = Ts + RTD/2.
     * 4. **Offset Calculation**: Determine the offset between Ts_est and client time (Tc) at response receipt. Since Tc equals Treceive, the offset is Ts_est - Treceive.
     * 5. **Aggregate Offset Update**: Update the aggregated offset value (offset_agg) by adding the new offset. Formula: offset_agg = offset_agg + offset.
     * 6. **Average Offset (cs_offset)**: After all samples are processed, calculate the average offset by dividing offset_agg by the number of samples (X). Formula: cs_offset = offset_agg / X.
     *
     * This method provides a reliable estimate of the client-server offset (cs_offset).
     *
     * Typically, cs_offset is calculated once and used for future Tcest calculations. However, if synchronization issues arise (due to network changes, clock drift, etc.), recalculating cs_offset may be beneficial.
     *
     * **Calculating Client-Side Estimated Server Time (Tcest)**
     *
     * The Tcest value, required for certain API endpoints (e.g., /hssp/play), is calculated as follows:
     *
     * Tcest = Tc + cs_offset
     *
     * where Tc is the current client time and cs_offset is the pre-calculated client-server offset.
     *
     * @returns any Server time.
     * @throws ApiError
     */
    public getServerTime(): CancelablePromise<{
        server_time: Timestamp;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/servertime',
        });
    }

}
